/**
 * Per-entity configuration cluster — substrate × input cell per scope §6.1.
 *
 * Renders cluster header + "Together:" line per scope §4.3 + 5 sub-region
 * cards tiled within the cell per Atlas Day 2 visual-layout commitment
 * A.1 + (b). Each card surfaces verb-framed substrate-driven content + cross-
 * cell pointer where applicable + click handler that opens sub-region inspect
 * view per B.3 (Phase 6 wires the actual inspect navigation; Phase 3 stubs).
 *
 * Layout within cluster cell: 5 cards stacked vertically per (b) ~80px target
 * height each. Compact verbose card pattern — icon + name + verb-framed line
 * + optional cross-cell pointer.
 *
 * Substrate fetched on mount via fetchPerEntitySubstrate (perEntitySubstrate.ts).
 * Each card receives the relevant substrate slice as props.
 */

import { useEffect, useState, type JSX } from 'react';
import {
  fetchPerEntitySubstrate,
  type PerEntitySubstrate,
} from '../../../lib/atlas/perEntitySubstrate';
import PerDistributorCard from './PerDistributorCard';
import PerSupplierCard from './PerSupplierCard';
import PerBrandCard from './PerBrandCard';
import PerManufacturerCard from './PerManufacturerCard';
import PerCategoryCard from './PerCategoryCard';

export default function PerEntityCluster(): JSX.Element {
  const [substrate, setSubstrate] = useState<PerEntitySubstrate | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    fetchPerEntitySubstrate()
      .then((s) => {
        if (!cancelled) setSubstrate(s);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      id="per-entity-configuration"
      data-cell-coordinate="substrate-input"
      data-region-type="cluster"
      className="border border-border bg-surface-1 rounded-lg p-4 space-y-3"
    >
      {/* Cluster header per scope §6.1 */}
      <div>
        <h2 className="text-sm font-semibold text-ink-bright">Per-entity configuration</h2>
        <p className="text-xs text-ink-secondary mt-1">
          Five entity types with config rows shaping engine inputs.
        </p>
        <p className="text-xs text-ink-secondary mt-1">
          <span className="font-semibold text-ink-bright">Together:</span> ISG&apos;s structural
          posture for who and what it buys through.
        </p>
      </div>

      {/* COMPOSABILITY: per-section "Last changed X days ago" indicator slot — wires post-Day 92 */}

      {/* 5 sub-region cards per scope §6.1.1-6.1.5 */}
      {error ? (
        <p className="text-xs text-ink-muted italic">Per-entity substrate currently unavailable.</p>
      ) : !substrate ? (
        <div className="space-y-2" aria-label="Loading per-entity substrate">
          <div className="h-16 bg-surface-4 rounded animate-pulse" />
          <div className="h-16 bg-surface-4 rounded animate-pulse" />
          <div className="h-16 bg-surface-4 rounded animate-pulse" />
          <div className="h-16 bg-surface-4 rounded animate-pulse" />
          <div className="h-16 bg-surface-4 rounded animate-pulse" />
        </div>
      ) : (
        <div className="space-y-2">
          <PerDistributorCard substrate={substrate.distributors} />
          <PerSupplierCard substrate={substrate.suppliers} />
          <PerBrandCard substrate={substrate.brands} />
          <PerManufacturerCard substrate={substrate.manufacturers} />
          <PerCategoryCard substrate={substrate.categories} />
        </div>
      )}
    </section>
  );
}
